import {
  CompanyIdentifier,
  CompanyIdentifierLookupDTO,
  parseCompanyIdentifier,
  parseCompanyName,
} from "@shaarp/common";

/** Where the lookup behind an identifier card stands. */
export type CompanyIdentifierLookupState =
  | { readonly status: "loading" }
  | { readonly status: "settled"; readonly result: CompanyIdentifierLookupDTO }
  | { readonly status: "failed" };

/** What the composer holds when the user sends, besides the text itself. */
export type CompanyIdentifierShortcutContext = {
  /**
   * The screen offers the shortcut: the standalone chat. A company's own chat
   * already knows which company it is about.
   */
  readonly isEnabled: boolean;
  /** The company page the card opens is reserved to the professional licence. */
  readonly canOpenCompanyPage: boolean;
  /** A tool the user forced (a CRM, the web) says what the message is for. */
  readonly hasForcedTool: boolean;
  readonly hasAttachments: boolean;
  readonly hasTactic: boolean;
  readonly hasCompanyContext: boolean;
};

/**
 * The identifier the composer shows a card for, or `null` when the message is
 * a question for the chat and nothing else.
 *
 * Only a message that is nothing but an identifier qualifies — the shared rule
 * decides that — and only when nothing else the user staged says the message
 * is meant for the model: a forced tool, an attachment, a tactic or a company
 * in context all keep it a question for the chat.
 *
 * A message no identifier fits is read as a company name, which is the loose
 * end of the rule: most short messages pass it. That costs nothing, because a
 * name is resolved against the companies on file alone, shows a card only
 * where one is named that, and — unlike the four others — never holds back a
 * send.
 */
export const companyIdentifierToOpen = (
  message: string,
  context: CompanyIdentifierShortcutContext,
): CompanyIdentifier | null => {
  if (!context.isEnabled || !context.canOpenCompanyPage) {
    return null;
  }
  if (
    context.hasForcedTool ||
    context.hasAttachments ||
    context.hasTactic ||
    context.hasCompanyContext
  ) {
    return null;
  }

  const identifier = parseCompanyIdentifier(message);
  if (identifier) {
    return identifier;
  }

  const name = parseCompanyName(message);
  return name ? { type: "name", value: name } : null;
};
